/**
 * Toast Notification System
 * 
 * Provides in-app notifications for various events
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import './Toast.css';

// Create context for toast notifications
const ToastContext = createContext();

// Toast types with their styling
const TOAST_TYPES = {
  success: { icon: '✅', color: '#10b981' },
  error: { icon: '❌', color: '#ef4444' },
  warning: { icon: '⚠️', color: '#f59e0b' },
  info: { icon: 'ℹ️', color: '#3b82f6' },
  prompt: { icon: '🛠️', color: '#8b5cf6' }
};

// Toast Provider Component
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };
    
    setToasts(prev => [...prev, toast]);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Expose methods globally for non-React access
  useEffect(() => {
    window.showToast = addToast;
    window.hideToast = removeToast;
    return () => {
      delete window.showToast;
      delete window.hideToast;
    };
  }, [addToast, removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// Hook to use toast notifications
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast Container Component
function ToastContainer({ toasts, onRemove }) {
  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

// Individual Toast Component
function ToastItem({ toast, onRemove }) {
  const [isExiting, setIsExiting] = useState(false);
  const typeConfig = TOAST_TYPES[toast.type] || TOAST_TYPES.info;

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div 
      className={`toast-item ${isExiting ? 'exiting' : ''}`}
      style={{ '--toast-color': typeConfig.color }}
    >
      <span className="toast-icon">{typeConfig.icon}</span>
      <span className="toast-message">{toast.message}</span>
      <button className="toast-close" onClick={handleRemove}>×</button>
    </div>
  );
}

export default ToastProvider;

